import type { Facing } from '../contracts/presentation-snapshot';
import type { Vec2 } from './vec2';
import { assertFiniteNumber, assertFiniteVec2 } from './vec2';

export interface LocalAabb {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

export type WorldAabb = LocalAabb;

export interface AabbOverlap {
  readonly x: number;
  readonly y: number;
}

export const assertLocalAabb = (box: LocalAabb, name = 'aabb'): void => {
  assertFiniteNumber(box.minX, `${name}.minX`);
  assertFiniteNumber(box.minY, `${name}.minY`);
  assertFiniteNumber(box.maxX, `${name}.maxX`);
  assertFiniteNumber(box.maxY, `${name}.maxY`);
  if (box.maxX < box.minX || box.maxY < box.minY) {
    throw new RangeError(`${name} dimensions must be non-negative.`);
  }
};

export const createLocalAabb = (minX: number, minY: number, maxX: number, maxY: number): LocalAabb => {
  const box = Object.freeze({ minX, minY, maxX, maxY });
  assertLocalAabb(box);
  return box;
};

export const toWorldAabb = (local: LocalAabb, root: Vec2, facing: Facing): WorldAabb => {
  assertLocalAabb(local);
  assertFiniteVec2(root, 'root');
  const minX = facing === 'RIGHT' ? root.x + local.minX : root.x - local.maxX;
  const maxX = facing === 'RIGHT' ? root.x + local.maxX : root.x - local.minX;
  return Object.freeze({
    minX,
    minY: root.y + local.minY,
    maxX,
    maxY: root.y + local.maxY,
  });
};

export const getAabbOverlap = (left: WorldAabb, right: WorldAabb): AabbOverlap => Object.freeze({
  x: Math.min(left.maxX, right.maxX) - Math.max(left.minX, right.minX),
  y: Math.min(left.maxY, right.maxY) - Math.max(left.minY, right.minY),
});

export const aabbsOverlap = (left: WorldAabb, right: WorldAabb): boolean => {
  const overlap = getAabbOverlap(left, right);
  return overlap.x > 0 && overlap.y > 0;
};
